import { t } from '../services/language-manager';
import { GenesysUnavailableError, SessionExpiredError } from '../services/session';

/**
 * Known wizard/toolbar codes whose sentence lives in the catalog.
 * Sub-codes (info fields) and conflict reasons are chosen by the caller.
 */
const SERVER_ERROR_KEYS: Record<string, string> = {
  WIZARD_OUTDATED: 'settings-actions-error-wizard-outdated',
  ACTION_TYPE_NOT_ALLOWED: 'settings-actions-error-type-not-allowed',
  DIVISION_IDS_INVALID: 'settings-actions-error-divisions',
  SECRET_TOO_LONG: 'settings-actions-error-secret-too-long',
  CONFIG_TOO_LARGE: 'settings-actions-error-config-too-large',
  BUSINESS_CONTEXT_TOO_LONG: 'settings-actions-error-business-context',
  CATALOG_TRANSFER_FAILED: 'settings-actions-catalog-transfer-error',
  SKU_NOT_FOUND: 'settings-actions-catalog-row-removed-meanwhile',
  CATALOG_CONFLICT: 'settings-actions-catalog-row-catalog-changed',
  LAST_PRODUCT: 'settings-actions-catalog-row-delete-last',
  SESSION_EXPIRED: 'session-expired-inline',
  GENESYS_UNAVAILABLE: 'error-genesys-unavailable',
};

export interface ServerErrorInput {
  code?: string;
  /** Server text, shown under the translated sentence when it is not a known code. */
  cause?: string;
  /** Catalog key for this spot's own sentence, when the code has no entry. */
  knownKey?: string;
  /** Keep `cause` under the translated sentence (toolbar reasons, HTTP lines). */
  keepDetail?: boolean;
}

/**
 * Translated sentence for a known code. Anything else is the generic
 * sentence with the server's own text on the next line. Catalog line
 * errors and a customer's webhook reply are not passed through here.
 */
export function serverErrorMessage(input: ServerErrorInput): string {
  const key = input.knownKey ?? (input.code ? SERVER_ERROR_KEYS[input.code] : undefined);
  if (key && !input.keepDetail) return t(key);
  const summary = key ? t(key) : t('error-server-generic');
  const detail = input.cause?.trim();
  if (!detail || detail === summary) return summary;
  return `${summary}\n${detail}`;
}

/**
 * Text for an error caught around a backend call. An expired session or a
 * Genesys outage gets its own sentence with no server detail; anything else
 * is this spot's sentence with the server's text under it.
 */
export function caughtErrorMessage(error: unknown, knownKey: string): string {
  if (error instanceof SessionExpiredError) return t('session-expired-inline');
  if (error instanceof GenesysUnavailableError) return t('error-genesys-unavailable');
  return error instanceof Error
    ? serverErrorMessage({ cause: error.message, knownKey, keepDetail: true })
    : t(knownKey);
}
